import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { machineApi, Machine, MachineSearchParams } from '../../services/machineApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  FETCH_MACHINES,
  FETCH_MACHINE_BY_ID,
  CREATE_MACHINE,
  UPDATE_MACHINE,
  DELETE_MACHINE,
  SEARCH_MACHINES,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/machineSlice';

// Fetch all Machines
function* fetchMachinesSaga(action: PayloadAction<MachineSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { pagination, searchParams } = state.machine;
    
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchParams.search || '',
      active: searchParams.active,
      maintenanceRequired: searchParams.maintenanceRequired,
      sortBy: searchParams.sortBy || 'Name',
      sortOrder: searchParams.sortOrder || 'asc',
      ...action.payload,
    };
    
    const response = yield call(machineApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Machines:', error);
    yield put(setError(error.message || 'Failed to fetch Machines'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch Machine by ID
function* fetchMachineByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const machine = yield call(machineApi.getById, action.payload);
    yield put(setSelectedItem(machine));
  } catch (error: any) {
    console.error('Error fetching Machine by ID:', error);
    yield put(setError(error.message || 'Failed to fetch Machine'));
  } finally {
    yield put(setLoading(false));
  }
}

// Create Machine
function* createMachineSaga(action: PayloadAction<Omit<Machine, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(machineApi.validation.validateMachine, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newMachine = yield call(machineApi.create, action.payload);
    yield put(addItem(newMachine));
    yield put(setSuccessMessage('Machine created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Machine:', error);
    yield put(setError(error.message || 'Failed to create Machine'));
  } finally {
    yield put(setLoading(false));
  }
}

// Update Machine
function* updateMachineSaga(action: PayloadAction<{ id: number; machine: Partial<Machine> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, machine } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(machineApi.validation.validateMachine, machine);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedMachine = yield call(machineApi.update, id, machine);
    yield put(updateItem(updatedMachine));
    yield put(setSuccessMessage('Machine updated successfully'));
  } catch (error: any) {
    console.error('Error updating Machine:', error);
    yield put(setError(error.message || 'Failed to update Machine'));
  } finally {
    yield put(setLoading(false));
  }
}

// Delete Machine
function* deleteMachineSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(machineApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Machine deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Machine'));
    }
  } catch (error: any) {
    console.error('Error deleting Machine:', error);
    yield put(setError(error.message || 'Failed to delete Machine'));
  } finally {
    yield put(setLoading(false));
  }
}

// Search Machines
function* searchMachinesSaga(action: PayloadAction<MachineSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(machineApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Machines:', error);
    yield put(setError(error.message || 'Failed to search Machines'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch statistics
function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(machineApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

// Bulk activate
function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(machineApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(machineApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Machines activated successfully'));
      // Refresh the list
      yield put({ type: FETCH_MACHINES });
    } else {
      yield put(setError(response.message || 'Failed to activate Machines'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Machines:', error);
    yield put(setError(error.message || 'Failed to activate Machines'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk deactivate
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(machineApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(machineApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Machines deactivated successfully'));
      // Refresh the list
      yield put({ type: FETCH_MACHINES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Machines'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Machines:', error);
    yield put(setError(error.message || 'Failed to deactivate Machines'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk delete
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(machineApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(machineApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Machines deleted successfully'));
      // Refresh the list
      yield put({ type: FETCH_MACHINES });
    } else {
      yield put(setError(response.message || 'Failed to delete Machines'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Machines:', error);
    yield put(setError(error.message || 'Failed to delete Machines'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Export to CSV
function* exportToCSVSaga(action: PayloadAction<MachineSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { searchParams } = state.machine;
    
    const params = {
      ...searchParams,
      ...action.payload,
    };
    
    const blob = yield call(machineApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `machines_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Machines exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Machines:', error);
    yield put(setError(error.message || 'Failed to export Machines'));
  } finally {
    yield put(setLoading(false));
  }
}

// Root Machine Saga
export function* machineSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_MACHINES, fetchMachinesSaga);
  yield takeEvery(FETCH_MACHINE_BY_ID, fetchMachineByIdSaga);
  yield takeEvery(CREATE_MACHINE, createMachineSaga);
  yield takeEvery(UPDATE_MACHINE, updateMachineSaga);
  yield takeEvery(DELETE_MACHINE, deleteMachineSaga);
  yield takeEvery(SEARCH_MACHINES, searchMachinesSaga);
  yield takeEvery(FETCH_STATISTICS, fetchStatisticsSaga);
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
} 